<script setup lang="ts">
import { type ComponentPublicInstance, onMounted, onUnmounted, ref, watch } from 'vue'
import { usePrevioDS } from '../../../../../../../composables/helpers'
import { PSpaceSize } from '../../../../../../space/types'

const { getSpacePx } = usePrevioDS().useSpace()

// Slot height = xl item content + xl inter-item gap
const ITEM_HEIGHT = getSpacePx(PSpaceSize.XL) * 2
const VISIBLE_ITEMS = 5
const PICKER_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS
const CENTER = 2
const BUFFER = 3
const TOTAL_SLOTS = VISIBLE_ITEMS + BUFFER * 2

type Props = {
  items: string[]
  selectedIndex: number
}

type Emits = {
  (e: 'update:selectedIndex', index: number): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const containerRef = ref<HTMLElement | null>(null)

// Plain array — not reactive; DOM refs for virtual slots
const slotElements: (HTMLElement | null)[] = Array(TOTAL_SLOTS).fill(null)

// Animation state — mutable locals, intentionally not reactive
let offset = -props.selectedIndex * ITEM_HEIGHT
let dragging = false
let startY = 0
let startOffset = 0
let rafId: number | null = null
let samples: { y: number; t: number }[] = []
let wasDrag = false

const setSlotRef = (el: Element | ComponentPublicInstance | null, index: number): void => {
  slotElements[index] = el instanceof Element ? (el as HTMLElement) : null
}

// % can return negative values in JS; this ensures the result is always positive
const wrapIndex = (n: number, m: number): number => ((n % m) + m) % m

const paint = (): void => {
  const count = props.items.length

  if (count === 0) {
    return
  }

  const centerContinuous = -offset / ITEM_HEIGHT
  const baseSlot = Math.floor(centerContinuous) - CENTER - BUFFER

  for (let slotIndex = 0; slotIndex < TOTAL_SLOTS; slotIndex++) {
    const slot = baseSlot + slotIndex
    const logicalIdx = wrapIndex(slot, count)
    const yPos = slot * ITEM_HEIGHT + offset + CENTER * ITEM_HEIGHT
    const dist = Math.abs(slot - centerContinuous)
    const scale = Math.max(0.72, 1 - dist * 0.07)
    const alpha = Math.max(0.15, 1 - dist * 0.3)

    const el = slotElements[slotIndex]

    if (el) {
      el.style.transform = `translateY(${yPos}px) scale(${scale})`
      el.style.opacity = String(alpha)
      el.textContent = props.items[logicalIdx]
      el.classList.toggle('ds-drum-column__slot--selected', dist < 0.5)
    }
  }
}

const springTo = (targetOffset: number): void => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
  }

  const stiffness = 0.14
  const damping = 0.72
  let velocity = 0
  const count = props.items.length

  const step = (): void => {
    const dx = targetOffset - offset
    velocity = velocity * damping + dx * stiffness
    offset += velocity
    if (Math.abs(dx) < 0.3 && Math.abs(velocity) < 0.3) {
      offset = targetOffset
      paint()
      emit('update:selectedIndex', wrapIndex(Math.round(-targetOffset / ITEM_HEIGHT), count))
      return
    }
    paint()
    rafId = requestAnimationFrame(step)
  }
  rafId = requestAnimationFrame(step)
}

const snapNearest = (fromOffset: number): void => {
  const slot = Math.round(-fromOffset / ITEM_HEIGHT)
  springTo(-slot * ITEM_HEIGHT)
}

const coast = (velocity: number): void => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
  }
  let v = velocity
  const friction = 0.965

  const step = (): void => {
    v *= friction
    offset += v
    if (Math.abs(v) < 0.8) {
      snapNearest(offset)
      return
    }
    paint()
    rafId = requestAnimationFrame(step)
  }
  rafId = requestAnimationFrame(step)
}

const onPointerDown = (event: PointerEvent): void => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
  }
  dragging = true
  wasDrag = false
  startY = event.clientY
  startOffset = offset
  samples = [{ y: event.clientY, t: performance.now() }]
  containerRef.value?.setPointerCapture(event.pointerId)
}

const onPointerMove = (event: PointerEvent): void => {
  if (!dragging) {
    return
  }
  const dy = event.clientY - startY
  if (Math.abs(dy) > 3) {
    wasDrag = true
  }
  offset = startOffset + dy
  paint()
  samples.push({ y: event.clientY, t: performance.now() })
  if (samples.length > 6) {
    samples.shift()
  }
}

const onPointerUp = (): void => {
  if (!dragging) {
    return
  }
  dragging = false
  let velocity = 0

  if (samples.length >= 2) {
    const last = samples[samples.length - 1]
    let reference = samples[0]
    for (let i = samples.length - 2; i >= 0; i--) {
      if (last.t - samples[i].t >= 40) {
        reference = samples[i]
        break
      }
    }
    const dt = last.t - reference.t
    if (dt > 0) {
      velocity = ((last.y - reference.y) / dt) * 16
    }
  }

  if (Math.abs(velocity) > 3) {
    coast(velocity)
  } else {
    snapNearest(offset)
  }
}

const onClick = (event: MouseEvent): void => {
  if (wasDrag) {
    return
  }
  const container = containerRef.value
  if (!container) {
    return
  }
  const rect = container.getBoundingClientRect()
  const clickY = event.clientY - rect.top
  const targetSlot = Math.floor((clickY - offset - CENTER * ITEM_HEIGHT) / ITEM_HEIGHT)
  springTo(-targetSlot * ITEM_HEIGHT)
}

watch(
  () => props.selectedIndex,
  (newIndex) => {
    const count = props.items.length
    if (count === 0) {
      return
    }
    const currentSlot = Math.round(-offset / ITEM_HEIGHT)
    let diff = newIndex - wrapIndex(currentSlot, count)
    if (diff > count / 2) {
      diff -= count
    }
    if (diff < -count / 2) {
      diff += count
    }
    offset = -(currentSlot + diff) * ITEM_HEIGHT
    paint()
  },
)

watch(() => props.items, paint)

onMounted(paint)

onUnmounted(() => {
  if (rafId === null) {
    return
  }

  cancelAnimationFrame(rafId)
})
</script>

<template>
  <div class="ds-drum-column">
    <div
      ref="containerRef"
      class="ds-drum-column__track"
      role="listbox"
      :style="{ height: `${PICKER_HEIGHT}px` }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @click="onClick"
    >
      <div class="ds-drum-column__fade ds-drum-column__fade--top" />
      <div class="ds-drum-column__fade ds-drum-column__fade--bottom" />
      <div
        v-for="i in TOTAL_SLOTS"
        :key="i"
        :ref="(el) => setSlotRef(el, i - 1)"
        class="ds-drum-column__slot"
        :style="{ height: `${ITEM_HEIGHT}px` }"
        role="option"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '../../../../../../../assets/scss/shared/_brand.module.scss';
@import '../../../../../../../assets/scss/shared/variables/ds.spacing';

.ds-drum-column {
  width: 90px;

  &__track {
    position: relative;
    overflow: hidden;
    cursor: grab;
    touch-action: none;
    user-select: none;

    &:active {
      cursor: grabbing;
    }
  }

  &__fade {
    position: absolute;
    left: 0;
    right: 0;
    height: $space-xl-mobile * 2 * 1.8;
    pointer-events: none;
    z-index: 2;

    &--top {
      top: 0;
      background: linear-gradient(to bottom, getColor('white'), transparent);
    }

    &--bottom {
      bottom: 0;
      background: linear-gradient(to top, getColor('white'), transparent);
    }
  }

  &__slot {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: getColor('black');
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    cursor: pointer;
    letter-spacing: 0.02em;
    will-change: transform, opacity, font-size;

    &--selected {
      font-size: 40px;
      line-height: 44px;
    }
  }
}
</style>
